// src/components/CadastroAluno.js
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import './CadastroAluno.css';

function CadastroAluno({ isEditing = false }) {
  const { register, handleSubmit, setValue, watch } = useForm();
  const navigate = useNavigate();
  const { matricula } = useParams();
  const [fotoPreview, setFotoPreview] = useState(null);
  const [fotoFile, setFotoFile] = useState(null);
  const [termoPreview, setTermoPreview] = useState(null);
  const [termoFile, setTermoFile] = useState(null);
  const [docPreview, setDocPreview] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [novaMatricula, setNovaMatricula] = useState('');
  const [idade, setIdade] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    const carregarDados = async () => {
      if (isEditing && matricula) {
        const { data, error } = await supabase
          .from('alunos')
          .select('*')
          .eq('matricula', matricula)
          .single();
        if (error) {
          setError('Erro ao carregar aluno: ' + error.message);
          return;
        }
        Object.entries(data).forEach(([key, value]) => {
          setValue(key, value);
        });
        setNovaMatricula(data.matricula);
        setIdade(data.idade);
        setFotoPreview(data.foto_url);
        setTermoPreview(data.Termo_Responsabilidade);
        setDocPreview(data.documento);
      } else {
        const { data: alunos, error } = await supabase
          .from('alunos')
          .select('matricula');
        if (error) {
          setError('Erro ao carregar matrícula: ' + error.message);
          return;
        }
        const novaMat = String(alunos.length + 1).padStart(3, '0');
        setNovaMatricula(novaMat);
      }
    };
    carregarDados();
  }, [isEditing, matricula, setValue]);

  const dataNascimento = watch('data_nascimento');
  useEffect(() => {
    if (dataNascimento) {
      const idadeCalculada = calcularIdade(dataNascimento);
      setIdade(idadeCalculada);
    }
  }, [dataNascimento]);

  const calcularIdade = (dataNascimento) => {
    const hoje = new Date();
    const nascimento = new Date(dataNascimento);
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mes = hoje.getMonth() - nascimento.getMonth();
    if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
      idade--;
    }
    return idade;
  };

  const handleFileChange = (e, setFile, setPreview) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadFile = async (file, path) => {
    const { error } = await supabase.storage
      .from('fotos-alunos')
      .upload(path, file, { upsert: true });
    if (error) throw error;
    return supabase.storage.from('fotos-alunos').getPublicUrl(path).data.publicUrl;
  };

  const onSubmit = async (data) => {
    setError(null);
    try {
      let fotoUrl = fotoPreview || '', termoUrl = termoPreview || '', docUrl = docPreview || '';
      if (fotoFile) fotoUrl = await uploadFile(fotoFile, `aluno-${novaMatricula}.jpg`);
      if (termoFile) termoUrl = await uploadFile(termoFile, `termo-${novaMatricula}.jpg`);
      if (docFile) docUrl = await uploadFile(docFile, `documento-${novaMatricula}.jpg`);

      const alunoData = {
        matricula: novaMatricula,
        nome: data.nome,
        nome_pai: data.nome_pai,
        nome_mae: data.nome_mae,
        data_nascimento: data.data_nascimento,
        idade,
        escola: data.escola,
        tipo_documento: data.tipo_documento,
        documento: docUrl,
        foto_url: fotoUrl,
        responavel: data.Responavel,
        termo_responsabilidade: termoUrl,
        categoria: data.Categoria,
      };

      let result;
      if (isEditing) {
        result = await supabase.from('alunos').update(alunoData).eq('matricula', novaMatricula);
      } else {
        result = await supabase.from('alunos').insert([alunoData]);
      }

      if (result.error) throw result.error;
      navigate('/lista');
    } catch (err) {
      console.error('Erro:', err);
      setError('Erro ao salvar aluno: ' + err.message);
    }
  };

  return (
    <div className="cadastro-container">
      <h1>{isEditing ? 'Editar Aluno' : 'Cadastro de Aluno'}</h1>
      {error && <p className="error-message">{error}</p>}
      <form onSubmit={handleSubmit(onSubmit)}>
        <label>Matrícula:</label>
        <input type="text" value={novaMatricula} disabled />

        <label>Nome Completo:</label>
        <input {...register('nome')} required />

        <label>Nome do Pai:</label>
        <input {...register('nome_pai')} />

        <label>Nome da Mãe:</label>
        <input {...register('nome_mae')} />

        <label>Data de Nascimento:</label>
        <input {...register('data_nascimento')} type="date" required />

        <label>Idade (Calculada):</label>
        <input type="text" value={idade} disabled />

        <label>Escola:</label>
        <input {...register('escola')} />

        <label>Responsável:</label>
        <input {...register('Responavel')} />

        <label>Categoria:</label>
        <input {...register('Categoria')} />

        <label>Tipo de Documento:</label>
        <select {...register('tipo_documento')}>
          <option value="RG">RG</option>
          <option value="CPF">CPF</option>
          <option value="Certidao">Certidão de Nascimento</option>
        </select>

        <div className="section-separator">Foto do Documento</div>
        <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, setDocFile, setDocPreview)} />
        {docPreview && <img src={docPreview} alt="Documento" className="foto-preview" />}

        <div className="section-separator">Foto do Aluno</div>
        <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, setFotoFile, setFotoPreview)} />
        {fotoPreview && <img src={fotoPreview} alt="Aluno" className="foto-preview" />}

        <div className="section-separator">Termo de Responsabilidade</div>
        <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, setTermoFile, setTermoPreview)} />
        {termoPreview && <img src={termoPreview} alt="Termo" className="foto-preview" />}

        <button type="submit">Salvar</button>
        <button onClick={() => navigate('/lista')} type="button">Cancelar</button>
      </form>
    </div>
  );
}

export default CadastroAluno;
